import { Flame, Users } from "lucide-react";

const seatsLeft = (s) => Math.max((s?.seats_total || 0) - (s?.seats_taken || 0), 0);
export const hasSeats = (s) => (s?.seats_total || 0) > 0;

const Bar = ({ pct }) => (
  <div className="h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
    <div
      className="h-full rounded-full bg-emerald-500 transition-[width] duration-700"
      style={{ width: `${pct}%` }}
    />
  </div>
);

/* "7 of 25 seats left this month" + progress bar */
export const SeatBar = ({ seats, className = "" }) => {
  if (!hasSeats(seats)) return null;
  const left = seatsLeft(seats);
  const pct = Math.round((seats.seats_taken / seats.seats_total) * 100);
  return (
    <div data-testid="seat-bar" className={`space-y-2 ${className}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-xs text-zinc-300">
          <span className="text-emerald-400 font-semibold" data-testid="seat-bar-left">
            {left}
          </span>{" "}
          of {seats.seats_total} seats left
          {seats.seats_period ? ` ${seats.seats_period}` : ""}
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-600">
          {pct}% filled
        </span>
      </div>
      <Bar pct={pct} />
    </div>
  );
};

/* small badge */
export const SeatBadge = ({ seats, className = "" }) => {
  if (!hasSeats(seats)) return null;
  return (
    <span
      data-testid="seat-badge"
      className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-800/70 bg-emerald-500/10 px-3 py-1 font-mono text-[11px] text-emerald-300 ${className}`}
    >
      <Flame className="h-3 w-3" /> Only {seatsLeft(seats)} seats left
    </span>
  );
};

/* progress bar + recent applicants */
export const SeatProof = ({ seats, className = "" }) => {
  if (!hasSeats(seats)) return null;
  const pct = Math.round((seats.seats_taken / seats.seats_total) * 100);
  return (
    <div
      data-testid="seat-proof"
      className={`border border-zinc-800 bg-zinc-950/70 rounded-sm p-4 space-y-2.5 ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-xs text-zinc-300">
          <span className="text-emerald-400 font-semibold">{seatsLeft(seats)}</span> of{" "}
          {seats.seats_total} seats left{seats.seats_period ? ` ${seats.seats_period}` : ""}
        </p>
        {seats.applicants_week > 0 && (
          <span
            data-testid="seat-applicants"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-500"
          >
            <Users className="h-3 w-3" /> {seats.applicants_week} traders applied this week
          </span>
        )}
      </div>
      <Bar pct={pct} />
    </div>
  );
};

/* sticky: bottom on mobile, top on desktop */
export const SeatSticky = ({ seats, onApply }) => {
  if (!hasSeats(seats)) return null;
  const left = seatsLeft(seats);
  const pct = Math.round((seats.seats_taken / seats.seats_total) * 100);
  return (
    <div
      data-testid="seat-sticky"
      className="fixed left-0 right-0 z-[60] bottom-0 md:bottom-auto md:top-14 border-t md:border-t-0 md:border-b border-emerald-900/60 bg-black/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-12 py-2.5 flex items-center gap-3">
        <Flame className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[11px] sm:text-xs text-zinc-300 truncate">
            <span className="text-emerald-400 font-semibold">{left}</span> of {seats.seats_total}{" "}
            seats left{seats.seats_period ? ` ${seats.seats_period}` : ""}
          </p>
          <div className="mt-1 hidden sm:block max-w-xs">
            <Bar pct={pct} />
          </div>
        </div>
        <button
          type="button"
          onClick={onApply}
          data-testid="seat-sticky-apply"
          className="shrink-0 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-bold transition-colors duration-200 active:scale-95"
        >
          Claim a seat
        </button>
      </div>
    </div>
  );
};
